/**
 * Shared utilities for vehicle analysis — used by both Processing.jsx (full analysis)
 * and partialAnalysis.js (targeted maintenance/valuation updates).
 */
import { getUserProfile } from '../utils/storage';
import { aiCall } from './aiProxy';

const DEV = import.meta.env.DEV;

/* ───────────────────── Constants ───────────────────── */

export const WEB_SEARCH_TOOL = {
  type: 'function',
  function: {
    name: 'web_search',
    description:
      'Search the web for recent vehicle sales data, auction results, and market valuations from automotive marketplaces like Bring a Trailer, Cars & Bids, Hemmings, eBay Motors, and others. Use this to find real comparable sales for accurate market pricing.',
    parameters: {
      type: 'object',
      properties: {
        query: {
          type: 'string',
          description:
            'The search query. Include specific vehicle details (year, make, model, trim) and target marketplace names for best results.',
        },
      },
      required: ['query'],
    },
  },
};

export const MAX_TOOL_ROUNDS = 6;

export const ANALYSIS_SYSTEM_PROMPT = `You are Pocket Mechanic, an AI automotive expert. Analyze the user's vehicle and provide maintenance insights, cost estimates, and market intelligence.

For market valuations, use web_search to find recent actual sales from: Bring a Trailer, Cars & Bids, Hemmings, eBay Motors sold listings. NEVER use KBB/NADA/Edmunds for collector, enthusiast, exotic, or modified vehicles. Search for completed auctions from the past 6-12 months. Run 3-4 varied searches targeting different sources.

If searches fail, set recent_sales to [] and note "Unable to find recent comparable sales data" in data_source. Do NOT fabricate data. For mainstream vehicles, supplement with general market data.

Adapt language to the owner's skill level (Beginner→safety-focused, Expert→peer-level technical). Factor in ZIP code for regional labor rates.

Respond ONLY with valid JSON (no markdown, no code fences). Structure:
{
  "health_score": number,
  "maintenance_due": [
    {
      "service": string,
      "urgency": "high"|"medium"|"low",
      "cost_dealer": string,
      "cost_shop": string,
      "cost_diy": string,
      "difficulty": "Beginner"|"Intermediate"|"Advanced"|"Expert",
      "time_estimate": string,
      "why_important": string,
      "warning": string
    }
  ],
  "market_value": {
    "current_value_range": string,
    "value_trend": string,
    "recent_sales": [string],
    "value_factors": string,
    "data_source": string,
    "service_impact": [
      {
        "service": string,
        "status": "completed" | "not_found" | "unknown",
        "impact": string
      }
    ]
  },
  "summary": string
}

MODEL-SPECIFIC VALUE ADJUSTMENTS:
Before providing your valuation, identify 3-5 known common issues or high-value services specific to this exact model (e.g., timing belt on interference engines, IMS bearing on Porsche, head gaskets on Subaru EJ engines, VANOS on BMW). For each:
- If the service log shows it HAS been completed: this is a value-add. Raise the FLOOR of the value range (buyers pay more for a sorted example).
- If the service log shows it has NOT been done (or no service log exists): this represents deferred risk. Lower the FLOOR of the value range accordingly.
- Trim-specific premiums (e.g., GTS vs base) should affect the CEILING of the range.

HEALTH SCORE GUIDANCE (0-100):
The health score reflects the vehicle's CURRENT MECHANICAL HEALTH — not its age or mileage.

Primary factor (~60% weight): MAINTENANCE COMPLIANCE
- Is the vehicle up to date on scheduled maintenance?
- Does the service history show consistent, proactive care?
- Are there critical deferred items (timing belt, fluids, brakes)?
- A vehicle with a complete, up-to-date service history should start at 85+ regardless of age or mileage.

Secondary factor (~25% weight): OUTSTANDING ISSUES
- How many high-urgency maintenance items are currently due?
- Deduct for each overdue critical service, scaled by urgency.
- A vehicle with zero high-urgency items and only a few medium items should score 80+.

Tertiary factor (~15% weight): AGE & MILEAGE RISK
- Higher mileage and age increase the probability of wear-related failures, but this is a MINOR modifier — not a primary driver.
- A well-maintained 200,000-mile vehicle should score 75-90. A neglected 30,000-mile vehicle with multiple overdue services should score 40-60.
- For classics and collector vehicles (20+ years old), age is essentially irrelevant to health — only maintenance history and current condition matter.

IMPORTANT: Never score below 70 solely because of high mileage or old age when the service history is strong and no critical items are overdue.`;

/* ───────────────────── Helpers ───────────────────── */

export function isAbortError(err) {
  return err.name === 'AbortError' || err.name === 'APIUserAbortError' || err.code === 'ABORT_ERR';
}

export function formatServiceLog(entries) {
  if (!entries || entries.length === 0) return '';
  const lines = entries.map((e) => {
    let line = `- ${e.date}`;
    if (e.mileage) line += ` @ ${Number(e.mileage).toLocaleString()} miles`;
    line += `: ${e.service}`;
    if (e.type) line += ` (${e.type}`;
    if (e.cost != null) line += `, $${Number(e.cost).toLocaleString()}`;
    if (e.type) line += ')';
    if (e.notes) line += ` — ${e.notes}`;
    return line;
  });
  return `\n\nCompleted Service Log (do NOT recommend these again):\n${lines.join('\n')}`;
}

export function buildVehicleContext(vehicle, serviceLog) {
  const profile = getUserProfile();

  const conditionGuidance = vehicle.condition
    ? `\nVehicle Condition: ${vehicle.condition}

CONDITION-BASED GUIDANCE:
Vehicle is in ${vehicle.condition} condition. Prioritize maintenance items based on this — a Fair condition car likely has deferred maintenance to address. A Concours car needs preventive-only services. Adjust comparable sales to account for condition differences. A Concours example commands 20-30% premium over Good condition. A Fair condition example should be 15-25% below market average. Factor this into your value range and price recommendation.`
    : '';

  const serviceLogText = formatServiceLog(serviceLog);

  const profileContext = profile
    ? `\n\nUser Profile Context:
- Location (ZIP): ${profile.zipCode || 'Unknown'} (adjust labor rates and climate-specific advice for this region)
- Mechanical Level: ${profile.mechanicalLevel || 'Unknown'}${profile.salaryRange && profile.salaryRange !== 'Prefer not to say' ? `\n- Income Range: ${profile.salaryRange}` : ''}${profile.primaryUseCases?.length ? `\n- Owner Use Cases: ${profile.primaryUseCases.join(', ')}` : ''}`
    : '';

  const tmuContext = vehicle.tmu
    ? `\n\nTMU (Total Mileage Unknown) FLAG SET: Vehicle odometer currently reads ${vehicle.mileage} miles, however the total mileage is UNKNOWN. This may be due to odometer replacement, rollback (legal restoration), disconnected odometer (track use), or unknown mileage history. Base maintenance recommendations primarily on TIME INTERVALS, not mileage. Example: "Annual oil change recommended" instead of "Oil change due in 3,000 miles". For mileage-critical services (timing belt, clutch), note that actual wear may differ from odometer reading.`
    : '';

  return { conditionGuidance, serviceLogText, profileContext, tmuContext };
}

export function buildFullUserPrompt(vehicle, serviceLog) {
  const { conditionGuidance, serviceLogText, profileContext, tmuContext } = buildVehicleContext(vehicle, serviceLog);

  return `Analyze this vehicle and provide a full maintenance and market report.

Year: ${vehicle.year}
Make: ${vehicle.make}
Model: ${vehicle.model}
Current Odometer: ${vehicle.mileage} miles${vehicle.tmu ? ' (TMU — Total Mileage Unknown)' : ''}${vehicle.vin ? `\nVIN: ${vehicle.vin}` : ''}
Last Service Date: ${vehicle.lastService || 'Unknown'}
Owner Skill Level: ${vehicle.skillLevel}
Primary Use: ${Array.isArray(vehicle.primaryUse) ? vehicle.primaryUse.join(', ') : vehicle.primaryUse}${vehicle.serviceHistory ? `\nService History Notes: ${vehicle.serviceHistory}` : ''}${tmuContext}${conditionGuidance}${profileContext}${serviceLogText}

Use web_search to find recent comparable sales before providing market valuation.`;
}

/**
 * Decode a data URL to plain text (for CSV, TXT, etc.).
 * Returns null if not decodable.
 */
function decodeDataUrlToText(dataUrl) {
  try {
    // data:[<mediatype>][;base64],<data>
    const match = dataUrl.match(/^data:([^;]+)?;?base64,(.+)$/);
    if (match) {
      return atob(match[2]);
    }
    // Non-base64 data URL
    const plainMatch = dataUrl.match(/^data:[^,]*,(.+)$/);
    if (plainMatch) {
      return decodeURIComponent(plainMatch[1]);
    }
    return null;
  } catch {
    return null;
  }
}

/* ───────────────── Service Record Extraction ───────────────── */

const SERVICE_EXTRACTION_SYSTEM_PROMPT = `You are a document extraction assistant. Your job is to extract structured service/maintenance records from uploaded files (receipts, invoices, CSV data, photos of service records).

For each service entry you find, extract:
- service: The name/description of the service performed (e.g. "Oil Change", "Brake Pad Replacement")
- date: The date of service in YYYY-MM-DD format. If only month/year, use the 1st of that month. If no date found, use null.
- mileage: The vehicle mileage at time of service (number, no commas). null if not found.
- cost: The cost of the service (number, no $ sign or commas). null if not found.
- type: The shop type - "DIY", "Independent", or "Dealer". Infer from context (dealership name = "Dealer", chain shops like Jiffy Lube = "Independent", home work = "DIY"). Default to "Independent" if unclear.
- shopName: The name of the shop/dealer if visible. null if not found.
- notes: Any additional relevant notes (parts used, warranty info, etc.). Empty string if none.

Respond ONLY with valid JSON (no markdown, no code fences). Structure:
{
  "entries": [
    {
      "service": string,
      "date": string|null,
      "mileage": number|null,
      "cost": number|null,
      "type": "DIY"|"Independent"|"Dealer",
      "shopName": string|null,
      "notes": string
    }
  ]
}

If no service entries can be extracted, return: { "entries": [] }
Extract EVERY entry you can find. A single CSV might have 50+ rows — extract them ALL.`;

/**
 * Extract structured service log entries from uploaded receipt photos/files.
 *
 * Two strategies depending on file type:
 *   - CSV / TSV / TXT: parse client-side, ask the AI only to map columns
 *     to our schema (single small call, no per-row AI cost). Handles
 *     files of any size — 10, 100, 10,000 rows all behave the same.
 *   - Images / PDFs / DOCX / XLSX: send through gpt-4o vision as before,
 *     since we need OCR or visual layout parsing.
 *
 * Returns a merged array from both strategies.
 */
export async function extractServiceRecords(files, vehicle, signal) {
  if (!files || files.length === 0) return [];

  const csvFiles = files.filter(isCsvLikeFile);
  const otherFiles = files.filter(f => !csvFiles.includes(f));

  const results = [];

  // ─── CSV path — parse + AI-map columns ────────────────────────────
  for (const file of csvFiles) {
    try {
      const text = decodeDataUrlToText(file.data);
      if (!text) continue;
      const entries = await extractFromCsvText(text, vehicle, signal);
      results.push(...entries);
    } catch (err) {
      if (isAbortError(err)) throw err;
      if (DEV) console.warn(`[PocketMechanic] CSV path failed for ${file.name}, falling back to vision:`, err.message);
      // Fall back to the vision path for this file so we don't just
      // drop it on the floor. The main scan can still succeed.
      otherFiles.push(file);
    }
  }

  // ─── Vision path for images / PDFs / anything not CSV-like ────────
  if (otherFiles.length > 0) {
    const entries = await extractViaVision(otherFiles, vehicle, signal);
    results.push(...entries);
  }

  if (DEV) console.log('[PocketMechanic] Extracted', results.length, 'total service entries');
  return results;
}

function isCsvLikeFile(file) {
  if (file.type === 'image') return false;
  const name = (file.name || '').toLowerCase();
  const mime = (file.mimeType || '').toLowerCase();
  return name.endsWith('.csv')
      || name.endsWith('.tsv')
      || name.endsWith('.txt')
      || mime === 'text/csv'
      || mime === 'text/tab-separated-values';
}

/**
 * Handle a single CSV/TSV file: parse locally, ask the AI to map columns,
 * then apply the mapping to every row locally so there's no per-row AI cost.
 */
async function extractFromCsvText(text, vehicle, signal) {
  const { header, rows } = parseCsv(text);
  if (rows.length === 0 || header.length === 0) return [];

  const mapping = await getCsvColumnMapping(header, rows.slice(0, 3), vehicle, signal);
  if (!mapping) return [];

  const entries = [];
  for (const row of rows) {
    const entry = applyMapping(row, header, mapping);
    // Require at least one meaningful field, otherwise the row was
    // probably empty padding at the end of the file.
    if (entry.service || entry.date || entry.mileage != null || entry.cost != null) {
      entries.push(entry);
    }
  }
  return entries;
}

/**
 * Minimal RFC 4180-ish CSV parser. Handles quoted fields, embedded
 * commas, doubled-up escaped quotes, and \r\n / \n line endings.
 * Auto-detects tabs as the separator (for .tsv files).
 */
function parseCsv(text) {
  // Sniff the delimiter — first non-quoted line's separator wins.
  const firstLine = text.split(/\r?\n/, 1)[0] || '';
  const separator = firstLine.includes('\t') && !firstLine.includes(',') ? '\t' : ',';

  const lines = [];
  let current = [];
  let field = '';
  let inQuotes = false;

  for (let i = 0; i < text.length; i++) {
    const ch = text[i];
    if (inQuotes) {
      if (ch === '"') {
        if (text[i + 1] === '"') { field += '"'; i++; }
        else inQuotes = false;
      } else field += ch;
    } else {
      if (ch === '"') inQuotes = true;
      else if (ch === separator) { current.push(field); field = ''; }
      else if (ch === '\n') { current.push(field); lines.push(current); current = []; field = ''; }
      else if (ch === '\r') { /* skip */ }
      else field += ch;
    }
  }
  if (field !== '' || current.length > 0) { current.push(field); lines.push(current); }

  const nonEmpty = lines.filter(l => l.some(cell => cell.trim() !== ''));
  if (nonEmpty.length === 0) return { header: [], rows: [] };

  const header = nonEmpty[0].map(s => s.trim());
  const rows = nonEmpty.slice(1);
  return { header, rows };
}

/**
 * Ask the AI which CSV columns map to which schema fields. One small
 * call regardless of CSV size — the mapping is applied to every row
 * locally in applyMapping().
 */
async function getCsvColumnMapping(header, sampleRows, vehicle, signal) {
  const promptSample = sampleRows.map((r, i) =>
    `${i + 1}: [${r.slice(0, header.length).map(v => JSON.stringify(v.slice(0, 60))).join(', ')}]`
  ).join('\n');

  const system = `You are a data mapping assistant. Given the column headers of a CSV containing vehicle service records, return a JSON object mapping our schema fields to the CSV column names.

Available fields:
- service: the name/description of what was done (e.g. "Oil Change")
- date: when the service happened
- mileage: odometer reading (numeric)
- cost: dollar amount (numeric)
- type: shop type ("DIY" | "Independent" | "Dealer") — use null if no column matches
- shopName: name of the shop/dealer
- notes: any additional notes

For each field, respond with either the exact column name from the CSV or null if there's no matching column. Respond with ONLY valid JSON, no markdown.

Example:
{"service": "Description", "date": "Date", "mileage": "Odometer", "cost": "Total", "type": null, "shopName": "Shop", "notes": "Comments"}`;

  const user = `CSV columns: ${JSON.stringify(header)}

Sample rows:
${promptSample}

Vehicle: ${vehicle.year} ${vehicle.make} ${vehicle.model}`;

  const { result } = await aiCall({
    action: 'chat_simple',
    creditCost: 0,
    model: 'gpt-4o-mini', // Small task, cheap model is plenty.
    messages: [
      { role: 'system', content: system },
      { role: 'user', content: user },
    ],
    temperature: 0,
    max_tokens: 400,
    timeout: 30000,
  }, { signal, timeout: 30000 });

  const cleaned = result.replace(/^```(?:json)?\s*/, '').replace(/\s*```$/, '').trim();
  try {
    const parsed = JSON.parse(cleaned);
    // Only keep entries where the column name actually exists in the header.
    const mapping = {};
    for (const [field, col] of Object.entries(parsed)) {
      if (col && header.includes(col)) mapping[field] = col;
    }
    return mapping;
  } catch {
    if (DEV) console.warn('[PocketMechanic] Column mapping response was not valid JSON:', cleaned.slice(0, 200));
    return null;
  }
}

/**
 * Apply the column mapping to a single CSV row, with light cleaning.
 * Dates get normalized to YYYY-MM-DD when parseable; numbers get
 * stripped of currency symbols and commas.
 */
function applyMapping(row, header, mapping) {
  function cell(field) {
    const col = mapping[field];
    if (!col) return null;
    const idx = header.indexOf(col);
    if (idx === -1) return null;
    const v = (row[idx] || '').trim();
    return v === '' ? null : v;
  }

  const rawDate = cell('date');
  const rawMileage = cell('mileage');
  const rawCost = cell('cost');
  const rawType = cell('type');

  return {
    service: cell('service') || 'Unknown Service',
    date: normalizeDate(rawDate),
    mileage: toIntOrNull(rawMileage),
    cost: toNumberOrNull(rawCost),
    type: normalizeShopType(rawType),
    shopName: cell('shopName'),
    notes: cell('notes') || '',
  };
}

function normalizeDate(s) {
  if (!s) return null;
  // Already YYYY-MM-DD?
  if (/^\d{4}-\d{2}-\d{2}$/.test(s)) return s;
  // Try Date() as a permissive fallback — handles MM/DD/YYYY, ISO w/ time, etc.
  const d = new Date(s);
  if (isNaN(d.getTime())) return null;
  return d.toISOString().slice(0, 10);
}

function toIntOrNull(s) {
  if (!s) return null;
  const cleaned = String(s).replace(/[^0-9-]/g, '');
  if (!cleaned) return null;
  const n = parseInt(cleaned, 10);
  return Number.isFinite(n) ? n : null;
}

function toNumberOrNull(s) {
  if (!s) return null;
  const cleaned = String(s).replace(/[^0-9.-]/g, '');
  if (!cleaned) return null;
  const n = parseFloat(cleaned);
  return Number.isFinite(n) ? n : null;
}

function normalizeShopType(s) {
  if (!s) return 'Independent';
  const v = s.toLowerCase();
  if (v.includes('diy') || v.includes('self')) return 'DIY';
  if (v.includes('dealer')) return 'Dealer';
  return 'Independent';
}

/**
 * Fallback vision-AI path — used for images, PDFs, DOCX, XLSX, and any
 * CSV where the smart path fell through. Same behavior as before.
 */
async function extractViaVision(files, vehicle, signal) {
  const parts = [
    { type: 'text', text: `Extract all service/maintenance records from the following uploaded files for a ${vehicle.year} ${vehicle.make} ${vehicle.model}.` },
  ];

  for (const file of files) {
    if (file.type === 'image') {
      parts.push({
        type: 'image_url',
        image_url: { url: file.data, detail: 'high' },
      });
    } else {
      const textContent = decodeDataUrlToText(file.data);
      if (textContent) {
        parts.push({
          type: 'text',
          text: `\n--- Uploaded File: ${file.name} ---\n${textContent}\n--- End of ${file.name} ---`,
        });
      }
    }
  }

  if (DEV) console.log('[PocketMechanic] Vision-extracting from', files.length, 'files');

  const { result } = await aiCall({
    action: 'chat_simple',
    creditCost: 0,
    model: 'gpt-4o',
    messages: [
      { role: 'system', content: SERVICE_EXTRACTION_SYSTEM_PROMPT },
      { role: 'user', content: parts },
    ],
    temperature: 0.2,
    max_tokens: 16000,
    timeout: 90000,
  }, { signal, timeout: 90000 });

  const cleaned = result.replace(/^```(?:json)?\s*/, '').replace(/\s*```$/, '');
  return parseEntriesTolerantly(cleaned);
}

/**
 * Parse the AI's response into an entries array, with a fallback for
 * responses that were truncated mid-JSON (rare after the max_tokens
 * bump, but the safety net is cheap). On clean input we JSON.parse
 * the whole envelope; on failure we walk the string, extract each
 * complete `{...}` object, and parse them individually — anything
 * cut off at the tail gets discarded and everything before it is
 * salvaged.
 */
function parseEntriesTolerantly(text) {
  try {
    const parsed = JSON.parse(text);
    return parsed.entries || [];
  } catch {
    // Fall through to salvage mode.
  }

  const entries = [];
  let depth = 0;
  let start = -1;
  for (let i = 0; i < text.length; i++) {
    const ch = text[i];
    if (ch === '{') {
      if (depth === 0) start = i;
      depth++;
    } else if (ch === '}') {
      depth--;
      if (depth === 0 && start !== -1) {
        const chunk = text.slice(start, i + 1);
        try {
          const obj = JSON.parse(chunk);
          // Skip the outer envelope — only push shaped service records.
          if (obj && (obj.service !== undefined || obj.date !== undefined)) {
            entries.push(obj);
          }
        } catch { /* skip malformed chunk */ }
        start = -1;
      }
    }
  }
  if (DEV && entries.length > 0) console.warn('[PocketMechanic] Response was truncated — salvaged', entries.length, 'entries');
  return entries;
}

export function normalizeAnalysis(raw) {
  const mv = raw.market_value || {};
  const isNewFormat = typeof mv === 'object' && mv.current_value_range;

  return {
    vehicleSummary: {
      year: raw.year,
      make: raw.make,
      model: raw.model,
    },
    healthScore: raw.health_score,
    healthSummary: raw.summary,
    maintenanceItems: (raw.maintenance_due || []).map((item) => ({
      service: item.service,
      urgency: item.urgency,
      description: item.why_important || '',
      costDealer: item.cost_dealer,
      costShop: item.cost_shop,
      costDIY: item.cost_diy,
      difficulty: item.difficulty,
      timeEstimate: item.time_estimate,
      warning: item.warning,
    })),
    marketValue: isNewFormat
      ? {
          estimate: mv.current_value_range,
          trend: mv.value_trend?.toLowerCase().includes('appreciat')
            ? 'appreciating'
            : mv.value_trend?.toLowerCase().includes('declin')
              ? 'declining'
              : 'stable',
          trendNote: mv.value_trend,
          recentSales: mv.recent_sales || [],
          valueFactors: mv.value_factors || '',
          dataSource: mv.data_source || '',
          serviceImpact: mv.service_impact || [],
        }
      : {
          estimate: typeof raw.market_value === 'string' ? raw.market_value : 'N/A',
          trend: raw.value_trend || 'stable',
          trendNote:
            raw.value_trend === 'stable'
              ? 'Holding value well for its age.'
              : raw.value_trend === 'appreciating'
                ? 'This model is gaining value — consider holding.'
                : 'Normal depreciation. Maintain service records to preserve resale value.',
          recentSales: [],
          valueFactors: '',
          dataSource: '',
          serviceImpact: [],
        },
  };
}

/**
 * Parse value snapshot numbers from a market value estimate string.
 * e.g. "$28,000 - $35,000" → { marketValueLow: 28000, marketValueHigh: 35000 }
 */
export function parseValueSnapshot(result) {
  const estimate = result.marketValue?.estimate || '';
  const nums = estimate.match(/[\d,]+/g)?.map((n) => parseInt(n.replace(/,/g, ''), 10)).filter(Boolean) || [];
  const marketValueLow = nums.length >= 2 ? Math.min(...nums) : (nums.length === 1 ? nums[0] : null);
  const marketValueHigh = nums.length >= 2 ? Math.max(...nums) : (nums.length === 1 ? nums[0] : null);
  const healthScore = typeof result.healthScore === 'number' ? result.healthScore : null;
  return { marketValueLow, marketValueHigh, healthScore };
}
